import React from "react";
import { Add, InfoOutlined } from "@mui/icons-material";
import { Button, IconButton } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";

import { BaseTabs } from "components/Shared/BaseTabs";

import { useAppMeta, useAuth, useTablePagination } from "hooks";
import { useLazyListPrepaymentQuery } from "store/services/PaymentsApi/prepaymentApi";
import HighlightedText from "components/Shared/HighlightedText";

const PrepaymentListScreen = () => {
  const { isVendor } = useAuth();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Prepayment" }];

  const prepaymentTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Prepayment ID",
      selector: (row) => row?.prepaymentID,
      cell: (row) => {
        const displayId = row?.prepaymentID;
        const path = !isVendor
          ? `/app/crm/prepayment/${row?._id}/detail`
          : `/app/vendor/prepayment/${row?._id}/detail`;
        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Profile",
      selector: (row) => row?.sourceModelRef?.name || "N/A",
      cell: (row) => {
        const name = row?.sourceModelRef?.name || "N/A";
        switch (row?.sourceModel) {
          case "VendorProfile":
            return (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>{name}</span>
                <small style={{ color: "gray" }}>Vendor Profile</small>
              </div>
            );
          case "Customer":
            return (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>{name}</span>
                <small style={{ color: "gray" }}>Customer Profile</small>
              </div>
            );
          case "Contractor":
            return (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>{name}</span>
                <small style={{ color: "gray" }}>Contractor Profile</small>
              </div>
            );
          default:
            return name;
        }
      },
    },
    {
      name: "Amount",
      selector: (row) => row?.amount,
      cell: (row) => row?.amount?.toFixed(2) || "N/A",
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link
            to={
              !isVendor
                ? `/app/crm/prepayment/${row?._id}/detail`
                : `/app/vendor/prepayment/${row?._id}/detail`
            }
          >
            <IconButton
              size="small"
              aria-label="View Prepayment"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
          {/* {!isVendor && (
            <Link to={`/app/crm/prepayment/${row?._id}/edit`}>
              <IconButton size="small" aria-label="Edit Prepayment">
                <ModeEditOutlineTwoTone />
              </IconButton>
            </Link>
          )} */}
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchPrepayments,
    { data: prepaymentRes, isLoading, isFetching, error },
  ] = useLazyListPrepaymentQuery();

  React.useEffect(() => {
    fetchPrepayments({
      active: activeTab === "Active" ? "true" : "false",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchPrepayments, keyword, perPage]);

  useAppMeta(`${activeTab} Prepayments`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        {!isVendor && (
          <BaseTabs tabs={prepaymentTabs} defaultValue={activeTab} />
        )}
        <TableFilter
          searchLabel="Prepayment ID"
          disabled={isLoading || isFetching}
        >
          <div className="font-medium"></div>
        </TableFilter>

        <AppTable
          columns={columns}
          data={prepaymentRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={prepaymentRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default PrepaymentListScreen;

const PageActions = () => {
  const { isVendor } = useAuth();

  return (
    <>
      {!isVendor && (
        <Link to="/app/crm/prepayment/create">
          <Button startIcon={<Add />} variant="contained" color="primary">
            Add Prepayment
          </Button>
        </Link>
      )}
    </>
  );
};
